/**
 * Утилиты для работы с редиректом после авторизации
 */

const REDIRECT_STORAGE_KEY = 'redirectAfterLogin';

/**
 * Сохраняет путь для редиректа после авторизации
 * Сохраняет полный путь включая query параметры и hash
 * 
 * @param path - Путь для сохранения (по умолчанию текущий путь из window.location)
 */
export function saveRedirectPath(path?: string): void {
    if (path) {
        // Если путь передан явно, сохраняем его
        sessionStorage.setItem(REDIRECT_STORAGE_KEY, path);
        return;
    }

    // Используем текущий путь из window.location
    const currentPath = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    // Полный путь с query и hash
    const fullPath = currentPath + search + hash;

    // Не сохраняем путь если это страница логина
    if (currentPath === '/login') {
        return;
    }

    // Сохраняем полный путь
    sessionStorage.setItem(REDIRECT_STORAGE_KEY, fullPath);
}

/**
 * Получает сохраненный путь для редиректа
 * @returns Сохраненный путь или null если не сохранен
 */
export function getRedirectPath(): string | null {
    return sessionStorage.getItem(REDIRECT_STORAGE_KEY);
}

/**
 * Удаляет сохраненный путь
 */
export function clearRedirectPath(): void {
    sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
}

/**
 * Получает и очищает сохраненный путь
 * @returns Сохраненный путь или null если не сохранен
 */
export function getAndClearRedirectPath(): string | null {
    const path = getRedirectPath();
    clearRedirectPath();
    return path;
}

/**
 * Валидирует путь для редиректа
 * Проверяет, что путь не является страницей логина и не пустой
 * 
 * @param path - Путь для валидации
 * @returns true если путь валидный для редиректа
 */
export function isValidRedirectPath(path: string | null): boolean {
    if (!path) {
        return false;
    }

    // Убрать query и hash для проверки
    const pathWithoutQuery = path.split('?')[0].split('#')[0];

    // Не валидны: пустой путь, путь логина
    if (
        pathWithoutQuery === '' ||
        pathWithoutQuery === '/login'
    ) {
        return false;
    }

    // Путь '/' валиден, если есть query параметры или hash
    if (pathWithoutQuery === '/') {
        const hasQuery = path.includes('?');
        const hasHash = path.includes('#');
        return hasQuery || hasHash;
    }

    return true;
}

/**
 * Получает валидный путь для редиректа
 * Если сохраненный путь не валидный, возвращает дефолтный
 * 
 * @param defaultPath - Дефолтный путь (по умолчанию '/')
 * @returns Валидный путь для редиректа
 */
export function getValidRedirectPath(defaultPath = '/'): string {
    const savedPath = getAndClearRedirectPath();

    if (savedPath && isValidRedirectPath(savedPath)) {
        return savedPath;
    }

    return defaultPath;
}

